import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { products, splitsList } from '../../../constants';
import useSplits from '../../../hooks/useSplits';
import { useGetAllProductsQuery } from '../../../services/entitlementsApi';
import PricingTermCard from './PricingTermCard';
import { BillingPrompt, IntroCopy, PricingCards, Form } from './index.styles';

const formatPrice = value => {
  if (!Number.isFinite(Number(value))) {
    return value;
  }
  return Number(value).toFixed(2).replace(/\.00$/, '');
};

const AnnualPricingOptions = ({
  billingStartDate,
  currentSubscription,
  monthlyPrice,
  resolvedUpgradeOffer,
  setSelectedPlanOverride,
  onPricingChange,
}) => {
  const [selectedPricingItem, setSelectedPricingItem] = useState('');
  const splits = useSplits([splitsList.annualPricing, splitsList.prodmoveNewDesign]);

  const isMovingToIdentity = resolvedUpgradeOffer?.family === products.identity;
  const isFlexCustomer = Boolean(currentSubscription?.planCode);
  const isNewDesignEnabled = !!splits[splitsList.prodmoveNewDesign];
  const isAnnualPricingSplit = !!splits[splitsList.annualPricing];
  const shouldFetchAnnualPlan =
    isAnnualPricingSplit && isNewDesignEnabled && isFlexCustomer && isMovingToIdentity && Number.isFinite(monthlyPrice);

  const { data: productsData } = useGetAllProductsQuery(undefined, { skip: !shouldFetchAnnualPlan });

  const identityAnnualPlan = useMemo(() => {
    if (!shouldFetchAnnualPlan) {
      return null;
    }

    const candidates = (productsData?.eligiblePlans ?? []).filter(
      plan => plan?.family === products.identity && plan?.term?.toUpperCase() === 'ANNUALLY'
    );

    if (!candidates.length) {
      return null;
    }

    const isTrialPlan = plan => (plan?.trialLength ?? 0) > 0;

    return (
      candidates.find(plan => (productsData?.isTrialEligible ? isTrialPlan(plan) : !isTrialPlan(plan))) ?? candidates[0]
    );
  }, [shouldFetchAnnualPlan, productsData]);

  const pricingItems = useMemo(() => {
    if (!shouldFetchAnnualPlan || !identityAnnualPlan) {
      return [];
    }

    const annualPrice = Number(identityAnnualPlan.price);
    if (!Number.isFinite(annualPrice)) {
      return [];
    }

    const monthlyItem = {
      item: 'id-monthly',
      planCode: resolvedUpgradeOffer?.planCode,
      billingFrequency: 'monthly',
      summaryLabel: 'Monthly Subscription',
      summaryPrice: monthlyPrice,
      cardProps: {
        id: 'id-monthly',
        termText: 'Monthly',
        priceText: `£${formatPrice(monthlyPrice)}`,
        frequencyText: '/month',
        billingText: `Billed on the ${billingStartDate.format('Do')} of every month`,
      },
    };

    const originalAnnualPrice = Number(identityAnnualPlan.originalPrice ?? monthlyPrice * 12);
    const discountPercentage =
      identityAnnualPlan.discountPercentage ??
      Math.round(((originalAnnualPrice - annualPrice) / originalAnnualPrice) * 100);
    const monthlyEquivalent = annualPrice / 12;

    const annualItem = {
      item: 'id-annual',
      planCode: identityAnnualPlan.planCode,
      billingFrequency: 'annual',
      summaryLabel: 'Annual Subscription',
      summaryPrice: annualPrice,
      cardProps: {
        id: 'id-annual',
        termText: 'Annual',
        priceText: `£${formatPrice(originalAnnualPrice)}`,
        frequencyText: '/year',
        billingText: `Billed on ${billingStartDate.format('D MMMM')} every year`,
        discountBadgeText: `Save ${discountPercentage}%`,
        discountedPriceText: `£${formatPrice(annualPrice)}`,
        monthlyEquivalentText: `£${formatPrice(monthlyEquivalent)}/month`,
      },
    };

    return [monthlyItem, annualItem];
  }, [billingStartDate, identityAnnualPlan, monthlyPrice, resolvedUpgradeOffer?.planCode, shouldFetchAnnualPlan]);

  useEffect(() => {
    if (!pricingItems.length) {
      setSelectedPricingItem('');
      setSelectedPlanOverride?.(null);
      onPricingChange(null);
      return;
    }

    if (!selectedPricingItem || !pricingItems.some(item => item.item === selectedPricingItem)) {
      const [defaultItem] = pricingItems;
      setSelectedPricingItem(defaultItem.item);
      setSelectedPlanOverride?.({
        planCode: defaultItem.planCode,
        price: defaultItem.summaryPrice,
        billingFrequency: defaultItem.billingFrequency,
      });
      onPricingChange(defaultItem);
    }
  }, [onPricingChange, pricingItems, selectedPricingItem, setSelectedPlanOverride]);

  const handleSelectPricing = itemId => {
    setSelectedPricingItem(itemId);
    const item = pricingItems.find(pricingItem => pricingItem.item === itemId);
    if (item) {
      setSelectedPlanOverride?.({
        planCode: item.planCode,
        price: item.summaryPrice,
        billingFrequency: item.billingFrequency,
      });
      onPricingChange(item);
    }
  };

  const selectedPricing =
    pricingItems.find(item => item.item === selectedPricingItem) ?? (pricingItems.length ? pricingItems[0] : null);

  if (!pricingItems.length) {
    return null;
  }

  return (
    <Form id="billing-period-options">
      {productsData?.isTrialEligible && (
        <IntroCopy>
          <BillingPrompt>Choose how you&rsquo;ll be billed after your free trial:</BillingPrompt>
        </IntroCopy>
      )}

      <PricingCards role="radiogroup" aria-label="Billing term options">
        {pricingItems.map(item => (
          <PricingTermCard
            key={item.item}
            {...item.cardProps}
            isSelected={item.item === selectedPricing?.item}
            onSelect={handleSelectPricing}
          />
        ))}
      </PricingCards>
    </Form>
  );
};

AnnualPricingOptions.propTypes = {
  billingStartDate: PropTypes.shape({
    format: PropTypes.func.isRequired,
  }).isRequired,
  currentSubscription: PropTypes.shape({
    planCode: PropTypes.string,
  }),
  monthlyPrice: PropTypes.number.isRequired,
  resolvedUpgradeOffer: PropTypes.shape({
    family: PropTypes.string,
    planCode: PropTypes.string,
  }),
  setSelectedPlanOverride: PropTypes.func,
  onPricingChange: PropTypes.func.isRequired,
};

AnnualPricingOptions.defaultProps = {
  currentSubscription: null,
  resolvedUpgradeOffer: null,
  setSelectedPlanOverride: undefined,
};

export default AnnualPricingOptions;
